import type { CopyFormatter, SupportedLocales } from './types'

export function isCopyFormatter(value: unknown): value is CopyFormatter {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__brand' in value &&
    (value as CopyFormatter).__brand === 'CopyFormatter'
  )
}

export function resolveVariables(
  vars: Record<string, unknown>,
  locale: SupportedLocales,
): Record<string, string | number> {
  const resolved: Record<string, string | number> = {}
  for (const [key, value] of Object.entries(vars)) {
    if (isCopyFormatter(value)) {
      resolved[key] = value.format(locale)
    } else {
      resolved[key] = value as string | number
    }
  }
  return resolved
}

/**
 * Formats a name as a possessive, with locale-aware rules.
 *
 * In English: "Jon" → "Jon’s", "James" → "James’"
 * In Danish/Norwegian/Swedish: "Jon" → "Jons"
 * In other languages: returns the name unchanged (the translator adds
 * prepositions like "de" in the template).
 */
export function possessiveName(name: string): CopyFormatter {
  return {
    __brand: 'CopyFormatter',
    format: (locale: SupportedLocales) => {
      const trimmed = name.trim()
      if (!trimmed) return ''

      const lang = new Intl.Locale(locale).language
      switch (lang) {
        case 'en':
          return trimmed.toLowerCase().endsWith('s')
            ? `${trimmed}’`
            : `${trimmed}’s`
        case 'da':
        case 'no':
        case 'nb':
        case 'nn':
        case 'sv':
          return `${trimmed}s`
        default:
          return trimmed
      }
    },
  }
}
